import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import TierBadge from './portfolio/TierBadge';
import TierBudgetCard from './portfolio/TierBudgetCard';
import Tier1AllocationChart from './portfolio/Tier1AllocationChart';
import Tier1HoldingsTruthPanel from './portfolio/Tier1HoldingsTruthPanel';
import Tier1PlanDossier from './portfolio/Tier1PlanDossier';
import Tier1ExecutionWorkbench from './portfolio/Tier1ExecutionWorkbench';
import Tier1SleeveGrid from './portfolio/Tier1SleeveGrid';
import {
  buildPendingPlanPreviewSleeves,
  sleeveTrackingErrorPct,
} from './portfolio/tier1PendingPlanPreview';
import {
  cockpitWeightsMapFromTargets,
  countPlanDeviationsFromCockpit,
  mergeCockpitWeightsIntoSleeves,
  mergeNeutralWeightsIntoSleeves,
  neutralWeightsMapFromAllocation,
} from './portfolio/tier1CockpitWeights';
import { fmtPct, fmtUsd } from './portfolio/format';
import TierPerformanceOverTimeChart from './portfolio/TierPerformanceOverTimeChart';
import {
  Tier1ImplementationReport,
  Tier1HoldingRow,
  Tier1Plan,
  Tier1PolicyHistoryEntry,
  TierBudgetSnapshot,
  findPendingTier1Plan,
  tier1PlanId,
} from '../types/tier1Portfolio';

interface Props {
  accountId: string;
  tierId: string;
  tierName: string;
  /** Tier override, else account default — controls automatic order execution. */
  autoApproveEffective: boolean;
}

const Tier1PortfolioPanel: React.FC<Props> = ({
  accountId,
  tierId,
  tierName,
  autoApproveEffective,
}) => {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<Tier1Plan | null>(null);
  const [pendingPlan, setPendingPlan] = useState<Tier1Plan | null>(null);
  const [implementation, setImplementation] = useState<Tier1ImplementationReport | null>(null);
  const [budget, setBudget] = useState<TierBudgetSnapshot | null>(null);
  const [ipsHistory, setIpsHistory] = useState<Tier1PolicyHistoryEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [ackMissingThesis, setAckMissingThesis] = useState(false);
  const [cockpitBySleeve, setCockpitBySleeve] = useState<Map<string, number>>(new Map());
  const [neutralBySleeve, setNeutralBySleeve] = useState<Map<string, number>>(new Map());
  const [cockpitAsOfLabel, setCockpitAsOfLabel] = useState<string | null>(null);

  const macroWeightsSource = pendingPlan?.weightsSource ?? plan?.weightsSource ?? 'ai';

  const pendingNeedsThesisAck = useMemo(() => {
    if (!pendingPlan?.thesisContext) return true;
    return !pendingPlan.thesisContext.sixMonthsPlus || !pendingPlan.thesisContext.oneMonth;
  }, [pendingPlan]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const active = await apiService.getTier1ActivePlan(accountId, tierId);
      const [plans, budgets, history, workbench] = await Promise.all([
        apiService.listTier1Plans(accountId, tierId),
        apiService.getTierBudgets(accountId),
        apiService.getTier1IpsHistory(accountId, tierId),
        active
          ? apiService.getTier1Workbench(accountId, tierId)
          : Promise.resolve(null),
      ]);
      setPlan(active as Tier1Plan | null);
      setPendingPlan(findPendingTier1Plan(plans, active as Tier1Plan | null));
      if (workbench) {
        setImplementation(workbench.implementation);
      } else {
        setImplementation(await apiService.getTier1Implementation(accountId, tierId));
      }
      setBudget(budgets.find((b) => b.tierId === tierId) ?? null);
      setIpsHistory(history);
      setAckMissingThesis(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load Tier 1 portfolio');
    } finally {
      setLoading(false);
    }
  }, [accountId, tierId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const alloc = await apiService.getMacroAllocation();
        if (cancelled) return;
        setNeutralBySleeve(neutralWeightsMapFromAllocation(alloc.sleeves));

        if (macroWeightsSource === 'rules') {
          setCockpitBySleeve(cockpitWeightsMapFromTargets(alloc.sleeves));
          setCockpitAsOfLabel(alloc.asOfLabel);
        } else {
          const ai = await apiService.getMacroAiAllocation();
          if (cancelled) return;
          const rec = ai.weights?.status === 'accepted' ? ai.weights : null;
          setCockpitBySleeve(
            rec ? cockpitWeightsMapFromTargets(rec.sleeves) : new Map(),
          );
          setCockpitAsOfLabel(ai.asOfLabel ?? rec?.asOfLabel ?? null);
        }
      } catch {
        if (!cancelled) {
          setCockpitBySleeve(new Map());
          setNeutralBySleeve(new Map());
          setCockpitAsOfLabel(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [macroWeightsSource]);

  const apiErrorMessage = (e: unknown, fallback: string) => {
    if (e && typeof e === 'object' && 'response' in e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data
        ?.message;
      if (msg) return msg;
    }
    return e instanceof Error ? e.message : fallback;
  };

  const generatePlan = async () => {
    setBusy('generate');
    setError(null);
    try {
      await apiService.generateTier1Plan(accountId, tierId, { replacePending: true });
      await load();
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Generate failed'));
    } finally {
      setBusy(null);
    }
  };

  const discardPending = async () => {
    setBusy('discard');
    setError(null);
    try {
      await apiService.discardPendingTier1Plans(accountId, tierId);
      await load();
    } catch (e: unknown) {
      setError(apiErrorMessage(e, 'Discard failed'));
    } finally {
      setBusy(null);
    }
  };

  const approvePlan = async (planId: string) => {
    setBusy('approve');
    try {
      await apiService.approveTier1Plan(
        planId,
        pendingNeedsThesisAck ? ackMissingThesis : undefined,
      );
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setBusy(null);
    }
  };

  const runReview = async () => {
    setBusy('review');
    try {
      await apiService.runTier1Review(accountId, tierId);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Review failed');
    } finally {
      setBusy(null);
    }
  };

  const registeredHoldings = useMemo(() => {
    if (!implementation) return [] as Tier1HoldingRow[];
    return implementation.sleeves.flatMap((s) => s.holdings);
  }, [implementation]);

  const pendingPlanPreview = useMemo(() => {
    if (!pendingPlan) return null;
    const tierBudgetUsd = budget?.tierBudgetUsd ?? implementation?.tierBudgetUsd ?? 0;
    const sleeves = buildPendingPlanPreviewSleeves(
      pendingPlan,
      implementation,
      tierBudgetUsd,
    );
    return {
      sleeves,
      trackingErrorPct: sleeveTrackingErrorPct(sleeves),
    };
  }, [pendingPlan, implementation, budget]);

  const registryBySleeve = useMemo(() => {
    const map = new Map<string, string | undefined>();
    if (!implementation?.sleeves) return map;
    for (const s of implementation.sleeves) {
      map.set(s.sleeveKey, s.registryStatus);
    }
    return map;
  }, [implementation]);

  const scrollToWorkbench = useCallback(() => {
    document.getElementById('tier1-execution-workbench')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const sleeveDisplay = useMemo(() => {
    const base =
      pendingPlanPreview ??
      (implementation
        ? {
            sleeves: implementation.sleeves,
            trackingErrorPct: implementation.sleeveTrackingErrorPct,
          }
        : null);
    if (!base) return null;
    let sleeves = mergeCockpitWeightsIntoSleeves(base.sleeves, cockpitBySleeve);
    sleeves = mergeNeutralWeightsIntoSleeves(sleeves, neutralBySleeve);
    return {
      ...base,
      sleeves,
    };
  }, [pendingPlanPreview, implementation, cockpitBySleeve, neutralBySleeve]);

  const showingPendingPolicy = !!pendingPlanPreview;

  const planVsCockpitDeviations = useMemo(() => {
    if (!showingPendingPolicy || !sleeveDisplay) return 0;
    return countPlanDeviationsFromCockpit(sleeveDisplay.sleeves);
  }, [showingPendingPolicy, sleeveDisplay]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <TierPerformanceOverTimeChart accountId={accountId} riskTier={1} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Tier 1 — {tierName}</h2>
            <TierBadge kind="tier1" />
          </div>
          <p className="text-sm text-gray-500">
            Registry slots (tier + sleeve) reconciled to Schwab — only tagged holdings count toward
            this tier budget. autoApprove:{' '}
            {autoApproveEffective
              ? 'on — rebalance orders run without manual approval'
              : 'off — SMS + approve rebalance proposals in dashboard'}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {pendingPlan ? (
            <button
              type="button"
              onClick={() => void discardPending()}
              disabled={!!busy}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {busy === 'discard' ? 'Discarding…' : 'Discard pending'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => void generatePlan()}
            disabled={!!busy}
            className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy === 'generate'
              ? 'Generating…'
              : pendingPlan
                ? 'Regenerate plan'
                : 'Generate plan'}
          </button>
          <button
            type="button"
            onClick={() => void runReview()}
            disabled={!!busy}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {busy === 'review' ? 'Running…' : 'Run review'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {budget ? (
        <TierBudgetCard
          label="Tier 1"
          tierBudgetUsd={budget.tierBudgetUsd}
          tierDeployedUsd={budget.tierDeployedUsd}
          tierAvailableUsd={budget.tierAvailableUsd}
          allocationPct={budget.allocationPct}
          extra={
            <div className="space-y-2">
              <div>
                <p className="text-xl font-semibold">
                  {sleeveDisplay ? fmtPct(sleeveDisplay.trackingErrorPct) : '—'}
                </p>
                <p className="text-xs text-gray-500">Sleeve tracking error</p>
              </div>
              {implementation?.registeredSlotCount != null ? (
                <p className="text-xs text-gray-500">
                  {implementation.registeredSlotCount} registry slots
                </p>
              ) : null}
              {implementation?.tierPolicyBasisUsd != null &&
              implementation.tierPolicyBasisUsd > 0 ? (
                <p className="text-xs text-gray-500">
                  Policy basis {fmtUsd(implementation.tierPolicyBasisUsd)}
                  {implementation.tierLiveBudgetUsd != null
                    ? ` · live slice ${fmtUsd(implementation.tierLiveBudgetUsd)}`
                    : ''}
                </p>
              ) : null}
              {implementation?.capitalDriftUsd != null &&
              implementation.capitalDriftUsd > 500 ? (
                <p className="text-xs text-amber-800">
                  Core above live NAV slice by {fmtUsd(implementation.capitalDriftUsd)} — anchor
                  hold; no auto-sell for drawdowns elsewhere.
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {implementation?.usingNeutralBaseline && !pendingPlan ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          No active Tier 1 plan — sleeve cards use macro neutral weights for reference only.
          Generate and approve a plan to compare against your IPS.
        </div>
      ) : null}

      {showingPendingPolicy ? (
        <div className="rounded-md border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
          <strong>Pending plan preview</strong> — <strong>New plan</strong> bars are the draft awaiting
          approval
          {plan ? (
            <>
              ; <strong>Current</strong> is your active IPS
            </>
          ) : null}{' '}
          ({pendingPlan?.weightsSource ?? 'macro'} weights
          {pendingPlan?.macroAsOfMonth ? ` · ${pendingPlan.macroAsOfMonth}` : ''}).{' '}
          <strong>Neutral</strong> → <strong>Cockpit</strong> is the macro layer;{' '}
          <strong>New plan</strong> is the account AI review (
          {pendingPlan?.planAuthor === 'ai_review' ? 'ai_review' : 'deterministic fallback'}
          ).
          {planVsCockpitDeviations === 0 ? (
            <>
              {' '}
              Targets match cockpit — sleeve rationale describes tilts vs neutral, not further
              account adjustments.
            </>
          ) : (
            <>
              {' '}
              {planVsCockpitDeviations} sleeve(s) differ from cockpit by account AI.
            </>
          )}{' '}
          Schwab actuals stay at zero until you approve the plan and rebalance orders run.
        </div>
      ) : null}

      {plan && !pendingPlan ? (
        <Tier1ExecutionWorkbench
          accountId={accountId}
          tierId={tierId}
          autoApproveEffective={autoApproveEffective}
          hasActivePlan={implementation?.hasActivePlan ?? true}
          registryBySleeve={registryBySleeve}
          onRefresh={load}
          busyExternal={!!busy}
        />
      ) : null}

      {pendingPlan ? (
        <Tier1PlanDossier
          plan={pendingPlan}
          title={plan ? 'New plan — AI investment review' : 'Pending plan — AI investment review'}
        />
      ) : plan?.summary ? (
        <Tier1PlanDossier plan={plan} title="Active plan narrative" />
      ) : null}

      {sleeveDisplay && sleeveDisplay.sleeves.length > 0 ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>
                {showingPendingPolicy ? 'Sleeve holdings (pending plan preview)' : 'Sleeve holdings'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tier1SleeveGrid
                sleeves={sleeveDisplay.sleeves}
                accountId={accountId}
                policyLabel={showingPendingPolicy ? 'New plan' : 'Policy'}
                rationaleBySleeve={
                  showingPendingPolicy ? pendingPlan?.rationaleBySleeve : undefined
                }
                showMacroBaselines={showingPendingPolicy}
                onSleeveClick={
                  showingPendingPolicy ? undefined : () => scrollToWorkbench()
                }
              />
              <Tier1AllocationChart
                sleeves={sleeveDisplay.sleeves}
                trackingErrorPct={sleeveDisplay.trackingErrorPct}
                cockpitAsOfLabel={cockpitAsOfLabel}
                policySeriesName={showingPendingPolicy ? 'New plan' : 'Policy'}
                showNeutral={showingPendingPolicy}
                showCurrentPolicy={showingPendingPolicy && !!plan}
              />
            </CardContent>
          </Card>
          {implementation ? (
            <Tier1HoldingsTruthPanel
              registeredHoldings={registeredHoldings}
              unmappedHoldings={implementation.unmappedHoldings}
              planSymbolsNotHeld={implementation.planSymbolsNotHeld}
            />
          ) : null}
        </>
      ) : null}

      {pendingPlan ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {plan ? 'New plan pending approval' : 'Plan pending approval'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingNeedsThesisAck ? (
              <label className="flex items-start gap-2 text-sm text-amber-800">
                <input
                  type="checkbox"
                  checked={ackMissingThesis}
                  onChange={(e) => setAckMissingThesis(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  6m+ and/or 1m thesis snapshots are missing — I acknowledge approving without
                  full thesis context.
                </span>
              </label>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void approvePlan(tier1PlanId(pendingPlan))}
                disabled={
                  !!busy ||
                  !tier1PlanId(pendingPlan) ||
                  (pendingNeedsThesisAck && !ackMissingThesis)
                }
                className="rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {busy === 'approve' ? 'Approving…' : 'Approve plan'}
              </button>
              <button
                type="button"
                onClick={() => void discardPending()}
                disabled={!!busy}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {busy === 'discard' ? 'Discarding…' : 'Discard pending'}
              </button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {plan?.investmentPolicyStatement ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Investment Policy (IPS v{plan.investmentPolicyStatement.policyVersion})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-700">
            <p className="text-xs text-gray-500">
              Frozen {plan.investmentPolicyStatement.frozenAt.slice(0, 10)} · hash{' '}
              {plan.investmentPolicyStatement.policySnapshotHash.slice(0, 12)}…
              {implementation?.policyIntegrityOk === false ? (
                <span className="ml-2 font-medium text-red-600">integrity check failed</span>
              ) : implementation?.policyIntegrityOk ? (
                <span className="ml-2 text-green-700">verified</span>
              ) : null}
            </p>
            <div className="grid gap-2 md:grid-cols-2">
              <IpsBlock label="Purpose" text={plan.investmentPolicyStatement.sections.purpose} />
              <IpsBlock label="Scope" text={plan.investmentPolicyStatement.sections.scope} />
              <IpsBlock label="Macro anchor" text={plan.investmentPolicyStatement.sections.macroAnchor} />
              <IpsBlock
                label="Sleeve targets"
                text={plan.investmentPolicyStatement.sections.sleeveTargetsSummary}
              />
              <IpsBlock
                label="Holdings"
                text={plan.investmentPolicyStatement.sections.holdingsPolicy}
              />
              <IpsBlock
                label="Thesis context"
                text={plan.investmentPolicyStatement.sections.thesisContext}
              />
              <IpsBlock
                label="Constraints"
                text={plan.investmentPolicyStatement.sections.constraints}
              />
              <IpsBlock
                label="Rebalance"
                text={plan.investmentPolicyStatement.sections.rebalanceCadence}
              />
              <IpsBlock
                label="Execution"
                text={plan.investmentPolicyStatement.sections.execution}
              />
            </div>
            <ul className="list-disc pl-5 text-xs text-gray-500">
              {plan.investmentPolicyStatement.disclaimers.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : plan ? (
        <Card>
          <CardHeader>
            <CardTitle>Draft policy ({plan.macroAsOfMonth})</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-600">
            <p>{plan.summary}</p>
            <p className="mt-2 text-xs text-amber-700">
              Approve to freeze IPS v{plan.policyVersion ?? 1} (hash{' '}
              {plan.policySnapshotHash?.slice(0, 12) ?? '—'}…).
            </p>
          </CardContent>
        </Card>
      ) : null}

      {ipsHistory.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>IPS history</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm text-gray-700">
              {ipsHistory.map((h) => (
                <li key={h.planId}>
                  v{h.policyVersion} · {h.macroAsOfMonth} · {h.status}
                  {h.supersedeReason ? ` · ${h.supersedeReason}` : ''}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

    </div>
  );
};

const IpsBlock: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <div className="rounded border border-gray-100 bg-gray-50 p-2">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-gray-800">{text}</p>
  </div>
);

export default Tier1PortfolioPanel;
